import type { OutcomeDefinition } from "@repo/app-types/entities/outcome";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { Textarea } from "@repo/ui/Inputs/textarea";
import { Label } from "@repo/ui/Label/label";
import { linkStyle } from "@repo/ui/Link/index";
import { type FC, useState } from "react";
import { twMerge } from "tailwind-merge";
import { FieldDiffView } from "./fieldDiffView.js";
import { FunnelCustomFieldSelect } from "./funnelCustomFieldSelect.js";
import { RevenueCustomFieldSelect } from "./revenueCustomFieldSelect.js";

interface OutcomeFieldsProps {
  outcome: OutcomeDefinition | null;
  /**
   * Baseline context to diff the current context against (live/published in
   * normal editing, the current outcome while previewing an agent suggestion).
   * When it differs from the displayed context, a "See changes" toggle appears.
   */
  diffBaselineContext?: string | null;
  isRevenueOutcomeActive: boolean;
  isFunnelOutcomeActive: boolean;
  onUpdateOutcome: (outcome: OutcomeDefinition) => void;
  isReadOnly: boolean;
  /**
   * Fill a bounded parent (the assessment editor's settings pane) with a
   * context field that scrolls internally. Pass `false` on a flowing page so
   * the field takes a fixed height and the page is the only scroll region.
   * @default true
   */
  fillsHeight?: boolean;
}

/**
 * Renders the per-outcome input: a contact custom-field picker for revenue
 * outcomes, or a free-form context textarea for every other outcome.
 */
type ContextTab = "edit" | "diff";

export const OutcomeFields: FC<OutcomeFieldsProps> = ({
  outcome,
  diffBaselineContext,
  isRevenueOutcomeActive,
  isFunnelOutcomeActive,
  onUpdateOutcome,
  isReadOnly,
  fillsHeight = true,
}) => {
  const [contextTab, setContextTab] = useState<ContextTab>("edit");

  if (isRevenueOutcomeActive) {
    return (
      <RevenueCustomFieldSelect
        outcome={outcome}
        onUpdateOutcome={onUpdateOutcome}
        isReadOnly={isReadOnly}
      />
    );
  }

  if (isFunnelOutcomeActive) {
    return (
      <FunnelCustomFieldSelect
        outcome={outcome}
        onUpdateOutcome={onUpdateOutcome}
        isReadOnly={isReadOnly}
      />
    );
  }

  if (!outcome) {
    return null;
  }

  const showContextDiffToggle =
    diffBaselineContext != null && diffBaselineContext !== outcome.context;

  return (
    <div className={fillsHeight ? "flex min-h-0 flex-1 flex-col" : undefined}>
      <div className="mb-1 flex min-h-8 items-center justify-between">
        <Label size={Size.SMALL}>Context</Label>
        {showContextDiffToggle ? (
          <Button
            variant={ButtonVariant.Tertiary}
            size={Size.MEDIUM}
            className={twMerge("cursor-pointer pr-0", linkStyle())}
            onClick={() => {
              setContextTab(contextTab === "edit" ? "diff" : "edit");
            }}
          >
            {contextTab === "edit" ? "See changes" : "Edit"}
          </Button>
        ) : null}
      </div>
      {showContextDiffToggle && contextTab === "diff" ? (
        <FieldDiffView
          className={
            fillsHeight ? "min-h-0 flex-1 overflow-y-auto" : "min-h-[400px]"
          }
          savedValue={diffBaselineContext}
          editedValue={outcome.context}
        />
      ) : (
        <Textarea
          value={outcome.context}
          onChange={(newValue) =>
            onUpdateOutcome({ ...outcome, context: newValue })
          }
          placeholder="Describe how to determine the outcome..."
          isDisabled={isReadOnly}
          className="w-full"
          fillHeight={fillsHeight}
          height="400px"
        />
      )}
    </div>
  );
};
